/**
 * Weekly review screen (Task 13).
 * Mandatory framing: weekly payments are optional cash management.
 */

import { useState } from 'react';
import { formatCentsDisplay } from '../../domain/recommend/explain';
import type { Cents } from '../../domain/money';
import { asCents } from '../../domain/money';
import { businessDate, type BusinessDate } from '../../domain/time';
import type { CheckingReserve } from '../../domain/checkingReserve';
import type { WeeklyReview, WeeklyPlanPayment } from '../../domain/weeklyReview';
import { WEEKLY_REVIEW_FRAMING } from '../../domain/weeklyReview';

export type WeeklyReviewScreenProps = {
  review: WeeklyReview;
  checking: CheckingReserve;
  /** Create planned payment + cash_reservations for selected week transactions. */
  onPlanPayment?: ((payload: WeeklyPlanPayment) => Promise<void>) | undefined;
  /** Persist manual checking_balance entry. */
  onUpdateCheckingBalance?:
    | ((args: {
        checking_balance_cents: Cents;
        checking_balance_updated_at: BusinessDate;
      }) => Promise<void>)
    | undefined;
  /** Civil date stamped on checking balance updates (tests inject). */
  today?: BusinessDate | undefined;
};

function formatMoney(cents: Cents | null): string {
  if (cents === null) return '—';
  return formatCentsDisplay(cents);
}

function parseAmountToCents(raw: string): Cents | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed);
  if (match === null) return null;
  const dollars = BigInt(match[1] ?? '0');
  const frac = match[2] ?? '';
  const centsPart = frac.length === 0 ? 0n : frac.length === 1 ? BigInt(frac) * 10n : BigInt(frac);
  return asCents(dollars * 100n + centsPart);
}

function CardWeekRow(props: {
  card: WeeklyReview['cards'][number];
  selected: ReadonlySet<string>;
  onToggle: (txnId: string) => void;
  onPlan: () => void;
  planDisabled: boolean;
}): JSX.Element {
  const { card } = props;
  return (
    <article
      data-testid="weekly-review-card"
      data-card-id={card.card_id}
      className="rounded-lg border border-slate-800 bg-slate-900/60 p-4"
    >
      <header className="mb-3">
        <h3 className="text-base font-medium text-slate-100">{card.label}</h3>
        <p className="mt-1 text-xs text-slate-500">
          Grace: {card.grace_status} — {card.grace_reason}
        </p>
      </header>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">Purchases</dt>
          <dd className="text-slate-100">{formatMoney(card.purchases_cents)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">Refunds / credits</dt>
          <dd className="text-slate-100">{formatMoney(card.refunds_credits_cents)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">Net new spend</dt>
          <dd className="text-slate-100">{formatMoney(card.net_new_spend_cents)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">Contractual due now</dt>
          <dd className="text-slate-100">
            {formatMoney(card.due_now_cents)}
            {card.next_due_date !== null ? ` by ${card.next_due_date}` : ''}
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">
            Payments already scheduled
          </dt>
          <dd className="text-slate-100">{formatMoney(card.payments_already_scheduled_cents)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">
            Suggested additional payment
          </dt>
          <dd className="text-slate-100">{formatMoney(card.suggested_additional_payment_cents)}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-xs uppercase tracking-wide text-slate-500">
            Checking reserve required
          </dt>
          <dd className="text-slate-100">{formatMoney(card.checking_reserve_required_cents)}</dd>
        </div>
      </dl>

      {card.transactions.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-1 border-t border-slate-800 pt-3">
          {card.transactions.map((t) => (
            <li key={t.id} className="flex items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                data-testid="weekly-txn-select"
                data-txn-id={t.id}
                checked={props.selected.has(t.id)}
                onChange={() => props.onToggle(t.id)}
              />
              <span>
                {t.posted_date} · {t.kind} · {formatMoney(t.amount_cents)}
                {t.already_reserved ? ' · reserved' : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-sm text-slate-500">No posted activity in this window.</p>
      )}

      <button
        type="button"
        data-testid="plan-weekly-payment"
        className="mt-3 rounded border border-slate-600 px-3 py-1.5 text-sm text-slate-100 disabled:opacity-40"
        disabled={props.planDisabled}
        onClick={props.onPlan}
      >
        Plan payment for selected
      </button>
    </article>
  );
}

export function WeeklyReviewScreen({
  review,
  checking,
  onPlanPayment,
  onUpdateCheckingBalance,
  today,
}: WeeklyReviewScreenProps): JSX.Element {
  const [selectedByCard, setSelectedByCard] = useState<Record<string, Set<string>>>({});
  const [balanceRaw, setBalanceRaw] = useState('');
  const [busy, setBusy] = useState(false);

  function selectedFor(cardId: string): Set<string> {
    return selectedByCard[cardId] ?? new Set();
  }

  function toggle(cardId: string, txnId: string): void {
    setSelectedByCard((prev) => {
      const next = new Set(prev[cardId] ?? []);
      if (next.has(txnId)) next.delete(txnId);
      else next.add(txnId);
      return { ...prev, [cardId]: next };
    });
  }

  async function planForCard(cardId: string): Promise<void> {
    if (onPlanPayment === undefined) return;
    const ids = [...selectedFor(cardId)];
    const payload = review.planPaymentForSelection(cardId, ids);
    if (payload === null) return;
    setBusy(true);
    try {
      await onPlanPayment(payload);
      setSelectedByCard((prev) => ({ ...prev, [cardId]: new Set() }));
    } finally {
      setBusy(false);
    }
  }

  async function submitCheckingBalance(): Promise<void> {
    if (onUpdateCheckingBalance === undefined) return;
    const cents = parseAmountToCents(balanceRaw);
    if (cents === null) return;
    const stamp =
      today ??
      (() => {
        const d = new Date();
        const y = d.getUTCFullYear();
        const m = String(d.getUTCMonth() + 1).padStart(2, '0');
        const day = String(d.getUTCDate()).padStart(2, '0');
        return businessDate(`${String(y)}-${m}-${day}`);
      })();
    setBusy(true);
    try {
      await onUpdateCheckingBalance({
        checking_balance_cents: cents,
        checking_balance_updated_at: stamp,
      });
      setBalanceRaw('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-testid="weekly-review-screen" className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-100">Weekly review</h2>
        <p data-testid="weekly-review-framing" className="mt-1 text-sm text-amber-200/90">
          {WEEKLY_REVIEW_FRAMING}
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Window {review.window_start} → {review.window_end}
        </p>
      </div>

      <div
        data-testid="checking-reserve-panel"
        className="rounded-lg border border-slate-800 bg-slate-900/40 p-4"
      >
        <h3 className="text-sm font-medium text-slate-200">Checking reserve</h3>
        <dl className="mt-2 grid grid-cols-2 gap-2 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500">Balance</dt>
            <dd className="text-slate-100">
              {formatMoney(checking.checking_balance_cents)}
              {checking.balance_is_stale ? (
                <span data-testid="checking-balance-stale" className="ml-2 text-xs text-amber-300">
                  stale
                </span>
              ) : null}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500">Committed</dt>
            <dd className="text-slate-100">{formatMoney(checking.committed_cents)}</dd>
          </div>
          <div className="col-span-2">
            <dt className="text-xs uppercase tracking-wide text-slate-500">Uncommitted</dt>
            <dd className="text-slate-100">
              {formatMoney(checking.uncommitted_cents)}
              {!checking.uncommitted_is_authoritative ? (
                <span
                  data-testid="uncommitted-not-authoritative"
                  className="ml-2 text-xs text-slate-500"
                >
                  not authoritative
                </span>
              ) : null}
            </dd>
          </div>
        </dl>
        {onUpdateCheckingBalance !== undefined ? (
          <div className="mt-3 flex gap-2">
            <input
              data-testid="checking-balance-input"
              className="flex-1 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100"
              placeholder="Checking balance"
              value={balanceRaw}
              onChange={(e) => setBalanceRaw(e.target.value)}
              disabled={busy}
            />
            <button
              type="button"
              data-testid="checking-balance-save"
              className="rounded border border-slate-600 px-3 py-1 text-sm text-slate-100 disabled:opacity-40"
              disabled={busy || parseAmountToCents(balanceRaw) === null}
              onClick={() => void submitCheckingBalance()}
            >
              Save
            </button>
          </div>
        ) : null}
      </div>

      {review.cards.length === 0 ? (
        <p className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-400">
          No cards in the portfolio yet.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {review.cards.map((card) => {
            const selected = selectedFor(card.card_id);
            const plan = review.planPaymentForSelection(card.card_id, [...selected]);
            return (
              <CardWeekRow
                key={card.card_id}
                card={card}
                selected={selected}
                onToggle={(id) => toggle(card.card_id, id)}
                onPlan={() => void planForCard(card.card_id)}
                planDisabled={busy || plan === null || onPlanPayment === undefined}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
